import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2, Megaphone, Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import {
  CAMPAIGN_ACTION_HINTS, CAMPAIGN_ACTION_LABELS, CAMPAIGN_STATE_LABELS, CAMPAIGN_STATE_TONES, CURRENCIES, HISTORY_LABELS, REASON_REQUIRED_ACTIONS,
  budgetLine, campaignPayload, campaignProblem, canManageCampaigns, emptyCampaignForm, formatBudget, marketingError, newKey, utmProblem,
} from "../lib/marketing";
import { formatDateTime } from "../lib/tickets";
import { formatDay } from "../lib/finance";
import CampaignSpend from "../components/marketing/CampaignSpend";
import { Field, OwnerSelect, StateBadge } from "../components/marketing/MarketingBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";

const ALL = "__all__";
const STATE_TABS = [["open", "En préparation et en cours"], ["ended", "Terminées"], ["archived", "Archivées"], ["all", "Toutes"]];

function CampaignDialog({ campaign, channels, onClose, onDone }) {
  const editing = !!campaign;
  const withBudget = !editing || campaign.budget !== undefined;
  const [form, setForm] = useState(() => emptyCampaignForm(campaign));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newKey);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setKey(newKey()); };
  const problem = campaignProblem(form);
  const setLine = (index, patch) => set({ budget: form.budget.map((l, i) => (i === index ? { ...l, ...patch } : l)) });
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = campaignPayload(form, withBudget);
      const { data } = editing
        ? await api.patch(`/marketing/campaigns/${campaign.id}`, { ...body, expected_version: campaign.version })
        : await api.post("/marketing/campaigns", body, { headers: { "Idempotency-Key": key } });
      toast.success(editing ? "Campagne modifiée" : "Campagne créée");
      onDone(data);
    } catch (e) {
      const existing = e?.response?.data?.detail?.existing;
      setError(existing ? `Une campagne porte déjà ce nom : ${existing.number} — ${existing.name}.` : marketingError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  const usedCurrencies = form.budget.map((l) => l.currency);
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="campaign-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier la campagne" : "Nouvelle campagne"}</DialogTitle>
          <DialogDescription>{editing ? campaign.number : "Une campagne rassemble une action de promotion : un canal, une période, un responsable."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="cmp-name">Nom</Label>
            <Input id="cmp-name" value={form.name} maxLength={160} onChange={(e) => set({ name: e.target.value })} className="h-10" data-testid="campaign-name" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Canal</Label>
              <Select value={form.channel || undefined} onValueChange={(channel) => set({ channel })}>
                <SelectTrigger className="h-10" aria-label="Canal" data-testid="campaign-channel"><SelectValue placeholder="Choisir un canal" /></SelectTrigger>
                <SelectContent>{channels.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Responsable</Label><OwnerSelect value={form.owner_id} onChange={(owner_id) => set({ owner_id })} testid="campaign-owner" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="cmp-start">Début</Label>
              <Input id="cmp-start" type="date" value={form.starts_on} onChange={(e) => set({ starts_on: e.target.value })} className="h-10" data-testid="campaign-start" /></div>
            <div className="space-y-1.5"><Label htmlFor="cmp-end">Fin (facultative)</Label>
              <Input id="cmp-end" type="date" value={form.ends_on} onChange={(e) => set({ ends_on: e.target.value })} className="h-10" data-testid="campaign-end" /></div>
          </div>
          {withBudget && (
            <div className="space-y-2" data-testid="campaign-budget">
              <Label className="block">Budget prévu</Label>
              {form.budget.map((line, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Select value={line.currency} onValueChange={(currency) => setLine(index, { currency })}>
                    <SelectTrigger className="h-10 w-28" aria-label="Devise"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.filter((c) => c === line.currency || !usedCurrencies.includes(c)).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={line.amount} inputMode="decimal" aria-label="Montant" onChange={(e) => setLine(index, { amount: e.target.value })} className="h-10 flex-1" data-testid={`campaign-budget-amount-${index}`} />
                  <Button type="button" variant="ghost" size="icon" aria-label="Retirer ce budget" onClick={() => set({ budget: form.budget.filter((_, i) => i !== index) })}><Trash2 size={16} /></Button>
                </div>
              ))}
              {form.budget.length < CURRENCIES.length && (
                <div><Button type="button" variant="outline" size="sm" data-testid="campaign-budget-add"
                  onClick={() => set({ budget: [...form.budget, budgetLine(CURRENCIES.find((c) => !usedCurrencies.includes(c)))] })}><Plus size={14} className="mr-1.5" />Ajouter un budget</Button></div>
              )}
              <p className="text-xs text-slate-500">Un budget par devise : elles ne sont jamais additionnées.</p>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            {[["utm_source", "UTM source"], ["utm_medium", "UTM medium"], ["utm_campaign", "UTM campagne"]].map(([id, label]) => (
              <div key={id} className="space-y-1.5"><Label htmlFor={`cmp-${id}`}>{label}</Label>
                <Input id={`cmp-${id}`} value={form[id]} maxLength={64} onChange={(e) => set({ [id]: e.target.value })} className="h-10" aria-invalid={!!utmProblem(form[id])} data-testid={`campaign-${id}`} /></div>
            ))}
          </div>
          <div className="space-y-1.5"><Label htmlFor="cmp-notes">Notes</Label>
            <Textarea id="cmp-notes" rows={2} maxLength={1000} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="campaign-notes" /></div>
          {error && <p className="text-sm text-red-600" role="alert" data-testid="campaign-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !!problem} title={problem || undefined} data-testid="campaign-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionDialog({ campaign, action, onClose, onDone }) {
  const needsReason = REASON_REQUIRED_ACTIONS.includes(action);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/marketing/campaigns/${campaign.id}/${action}`, { expected_version: campaign.version, reason: reason.trim() || null });
      toast.success(`${CAMPAIGN_ACTION_LABELS[action]} : c'est fait`);
      onDone(data);
    } catch (e) {
      setError(marketingError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="campaign-action-dialog">
        <DialogHeader>
          <DialogTitle>{CAMPAIGN_ACTION_LABELS[action]} « {campaign.name} » ?</DialogTitle>
          <DialogDescription>{CAMPAIGN_ACTION_HINTS[action]}</DialogDescription>
        </DialogHeader>
        {needsReason && (
          <div className="space-y-1.5"><Label htmlFor="cmp-reason">Motif</Label>
            <Textarea id="cmp-reason" rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="campaign-reason" /></div>
        )}
        {error && <p className="text-sm text-red-600" role="alert" data-testid="campaign-action-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || (needsReason && reason.trim().length < 3)} data-testid="campaign-action-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{CAMPAIGN_ACTION_LABELS[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignSheet({ id, channels, canManage, onClose, onChanged }) {
  const [campaign, setCampaign] = useState(null);
  const [phase, setPhase] = useState("loading");
  const { user } = useAuth();
  const [dialog, setDialog] = useState(null);
  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get(`/marketing/campaigns/${id}`);
      setCampaign(data);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [id]);
  useEffect(() => { if (id) load(); }, [id, load]);
  const changed = () => { setDialog(null); load(); onChanged(); };
  return (
    <Sheet open={!!id} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="campaign-sheet">
        <SheetHeader>
          <SheetTitle className="pr-6 text-left">{campaign?.name || "Campagne"}</SheetTitle>
          <SheetDescription className="text-left">{campaign ? `${campaign.number} · ${campaign.channel_label}` : ""}</SheetDescription>
        </SheetHeader>
        {phase === "loading" && <div className="space-y-2 py-4"><Skeleton className="h-6 w-full" /><Skeleton className="h-24 w-full" /></div>}
        {phase === "error" && <p className="py-4 text-sm text-slate-600" data-testid="campaign-sheet-error">Impossible de charger cette campagne.<Button variant="link" onClick={load}>Réessayer</Button></p>}
        {phase === "ready" && campaign && (
          <div className="space-y-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <StateBadge label={CAMPAIGN_STATE_LABELS[campaign.state]} tone={CAMPAIGN_STATE_TONES[campaign.state]} testid="campaign-state" />
              {canManage && campaign.state !== "archived" && <Button variant="outline" size="sm" onClick={() => setDialog("edit")} data-testid="campaign-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>}
              {canManage && campaign.actions.map((a) => (
                <Button key={a} variant={a === "activate" ? "default" : "outline"} size="sm" onClick={() => setDialog(a)} data-testid={`campaign-act-${a}`}>{CAMPAIGN_ACTION_LABELS[a]}</Button>
              ))}
              {campaign.state === "draft" && hasPermission(user, "marketing:delete") && <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDialog("delete")} data-testid="campaign-delete">Supprimer…</Button>}
            </div>
            <dl className="divide-y divide-slate-100">
              <Field label="Période">{formatDay(campaign.starts_on)}{campaign.ends_on ? ` → ${formatDay(campaign.ends_on)}` : " (sans fin prévue)"}</Field>
              <Field label="Responsable">{campaign.owner.name || "—"}</Field>
              {campaign.budget !== undefined && <Field label="Budget prévu">{formatBudget(campaign.budget) || <span className="text-slate-400">Non renseigné</span>}</Field>}
              {(campaign.utm.source || campaign.utm.medium || campaign.utm.campaign) && (
                <Field label="Paramètres UTM"><span className="font-mono text-xs">{["source", "medium", "campaign"].map((k) => campaign.utm[k] && `${k}=${campaign.utm[k]}`).filter(Boolean).join(" & ")}</span></Field>
              )}
              {campaign.notes && <Field label="Notes"><span className="whitespace-pre-wrap">{campaign.notes}</span></Field>}
            </dl>
            {campaign.budget !== undefined && <CampaignSpend campaignId={campaign.id} canManage={canManage && campaign.state !== "archived"} />}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Historique</h3>
              <ol className="space-y-2" data-testid="campaign-history">
                {campaign.history.map((h) => (
                  <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm text-slate-900">{HISTORY_LABELS[h.kind] || h.kind}</div>
                    <div className="text-xs text-slate-500">{formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                    {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
        <DeleteWithReasonDialog
          target={dialog === "delete" ? campaign : null} testId="delete-campaign" onClose={() => setDialog(null)}
          title={`Supprimer la campagne ${campaign?.number} ?`}
          description="Elle va dans la corbeille et se restaure pendant 30 jours. Une campagne activée, rattachée à des pharmacies ou à des dépenses, s'archive à la place."
          onConfirm={async (reason) => {
            try {
              await api.delete(`/marketing/campaigns/${campaign.id}`, { data: { reason } });
              toast.success("Mis à la corbeille");
              onClose();
              onChanged();
            } catch (e) {
              toast.error(marketingError(e, "Impossible de supprimer cet élément."));
              setDialog(null);
            }
          }}
        />
        {dialog === "edit" && campaign && <CampaignDialog campaign={campaign} channels={channels} onClose={() => setDialog(null)} onDone={changed} />}
        {dialog && dialog !== "edit" && dialog !== "delete" && campaign && <ActionDialog campaign={campaign} action={dialog} onClose={() => setDialog(null)} onDone={changed} />}
      </SheetContent>
    </Sheet>
  );
}

export default function MarketingCampaigns() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const state = STATE_TABS.some(([id]) => id === params.get("state")) ? params.get("state") : "open";
  const channel = params.get("channel") || "";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0, channels: [] });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const seq = useRef(0);
  const canManage = canManageCampaigns(user);
  const location = useLocation();
  const navigate = useNavigate();
  // « Nouvelle campagne » from the quick search lands here with router state, consumed once.
  useEffect(() => {
    if (!location.state?.openCreate) return;
    if (canManage) setCreating(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location, canManage, navigate]);

  const update = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => ({ limit: 200, state, ...(channel ? { channel } : {}), ...(search ? { q: search } : {}) }), [state, channel, search]);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/marketing/campaigns", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(marketingError(e, "Impossible de charger les campagnes."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const filtered = !!(channel || search);
  const reset = () => { setSearchInput(""); setParams({}, { replace: true }); };
  const showBudget = data.items.some((c) => c.budget !== undefined);

  return (
    <div className="space-y-4" data-testid="marketing-campaigns-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Campagnes</h1>
          <p className="text-sm text-slate-500">Les actions menées pour faire connaître Jamora : canal, période, responsable et budget.</p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)} data-testid="campaign-new"><Plus size={16} className="mr-1.5" />Nouvelle campagne</Button>}
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible" role="tablist" aria-label="Campagnes">
        {STATE_TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={state === id} onClick={() => update({ state: id === "open" ? "" : id })} data-testid={`campaigns-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap shrink-0 ${state === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{label}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Nom de la campagne…" className="pl-9 h-9" aria-label="Rechercher une campagne" data-testid="campaigns-search" />
        </div>
        <Select value={channel || ALL} onValueChange={(v) => update({ channel: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-48" aria-label="Canal" data-testid="campaigns-channel"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tous les canaux</SelectItem>{data.channels.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="campaigns-empty">
          <Megaphone className="mx-auto text-slate-300" size={26} />
          {filtered ? <><h3 className="font-medium text-slate-900">Aucune campagne ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button></> : (
            <><h3 className="font-medium text-slate-900">Aucune campagne pour le moment</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">{canManage ? "Créez votre première campagne : vous saurez ensuite combien de pharmacies chacune a amenées." : "Les campagnes apparaîtront ici."}</p>
              {canManage && <Button size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1.5" />Nouvelle campagne</Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="campaigns-table">
            <Table>
              <TableHeader><TableRow><TableHead>Campagne</TableHead><TableHead>Canal</TableHead><TableHead>Période</TableHead><TableHead>Responsable</TableHead>{showBudget && <TableHead className="hidden lg:table-cell">Budget prévu</TableHead>}<TableHead>État</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => setOpen(c.id)} data-testid={`campaign-row-${c.number}`}>
                    <TableCell><div className="font-medium text-slate-900">{c.name}</div><div className="text-xs text-slate-500 tabular-nums">{c.number}</div></TableCell>
                    <TableCell className="text-sm">{c.channel_label}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{formatDay(c.starts_on)}{c.ends_on ? ` → ${formatDay(c.ends_on)}` : ""}</TableCell>
                    <TableCell className="text-sm">{c.owner.name || "—"}</TableCell>
                    {showBudget && <TableCell className="hidden lg:table-cell text-sm">{formatBudget(c.budget) || <span className="text-slate-400">—</span>}</TableCell>}
                    <TableCell><StateBadge label={CAMPAIGN_STATE_LABELS[c.state]} tone={CAMPAIGN_STATE_TONES[c.state]} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="campaigns-cards">
            {data.items.map((c) => (
              <button key={c.id} type="button" onClick={() => setOpen(c.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`campaign-card-${c.number}`}>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.number} · {c.channel_label}</div>
                  <div className="text-sm text-slate-700">{formatDay(c.starts_on)}{c.ends_on ? ` → ${formatDay(c.ends_on)}` : ""}</div>
                  <StateBadge label={CAMPAIGN_STATE_LABELS[c.state]} tone={CAMPAIGN_STATE_TONES[c.state]} />
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </button>
            ))}
          </Card>
          <p className="text-xs text-slate-500" data-testid="campaigns-count">{data.total} campagne{data.total > 1 ? "s" : ""}</p>
        </>
      )}

      {creating && <CampaignDialog channels={data.channels} onClose={() => setCreating(false)} onDone={(c) => { setCreating(false); load(); setOpen(c.id); }} />}
      <CampaignSheet id={open} channels={data.channels} canManage={canManage} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
